import { Box, VStack } from '@chakra-ui/react';
import {
  NotificationTemplateStatus,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import { SIDEBAR_WIDTH_COLLAPSED } from '~/apps/consumer/components/PageDetailLayout/SidebarItem';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { PartnerHeader } from './components/PartnerOverlay/PartnerHeader';
import { SideToggle } from './components/PartnerOverlay/SideToggle';
import { PipelineKanban } from './components/Pipeline/PipelineKanban';
import { useNotificationsContext } from './context/NotificationsContext';
import { useNotificationTemplates } from './hooks/useNotificationTemplates';

export const PartnerNotificationsPage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { partners, loadingPartners } = useNotificationsContext();
  const [selectedSide, setSelectedSide] = useState<TransactionSide>(
    TransactionSide.BUY
  );

  const partner = partners.find((p) => p.id === partnerId);

  // Fetch templates to get counts
  const { templates } = useNotificationTemplates({
    partnerId: partnerId ?? '',
    side: selectedSide,
    skip: !partnerId,
  });

  // Calculate draft and unapproved counts
  const { draftCount, unapprovedCount } = useMemo(() => {
    let drafts = 0;
    let unapproved = 0;

    templates.forEach((template) => {
      if (template.status === NotificationTemplateStatus.DRAFT) {
        drafts++;
      }
      if (template.status === NotificationTemplateStatus.PENDING_APPROVAL) {
        unapproved++;
      }
    });

    return { draftCount: drafts, unapprovedCount: unapproved };
  }, [templates]);

  if (loadingPartners) {
    return <CenterSpinner />;
  }

  if (!partner) {
    return null;
  }

  return (
    <Box
      bg="indigo.900"
      height="100%"
      left={SIDEBAR_WIDTH_COLLAPSED}
      position="fixed"
      right={0}
      top={0}
      zIndex={1}
    >
      <VStack
        alignItems="stretch"
        height="100%"
        maxWidth="1800px"
        mx="auto"
        spacing={0}
      >
        <Box pl={{ base: 0, md: 6, lg: 12 }}>
          <PartnerHeader
            draftCount={draftCount}
            partner={partner}
            unapprovedCount={unapprovedCount}
          />
          <SideToggle
            selectedSide={selectedSide}
            onSideChange={setSelectedSide}
          />
        </Box>

        <Box flex={1} overflow="hidden">
          <PipelineKanban partnerId={partnerId} side={selectedSide} />
        </Box>
      </VStack>
      <Outlet />
    </Box>
  );
};
